<template>
  <FormContainer class="register__organization-credential-data-form__form-container">
    <div class="organization-credential-data-form__text-block">
      <h2 class="organization-credential-data-form__title">Вітаю в UConnect</h2>
      <p class="organization-credential-data-form__description">Ми надійна CRM-платформа для агентів і партнерських мереж</p>
    </div>
    <StatusProgress
      class="organization-credential-data-form__progress"
      :current-step="getCurrentStep"
    />
    <form class="organization-credential-data__form" @submit.prevent="handleSubmit">
      <LabeledTextInput
        class="organization-credential-data__input--email"
        label="Email"
        placeholder="example@email.com"
        v-model="form.email"
        :error-messages="errors.email ? [errors.email] : []"
        @input="clearError('email')"
      />
      <p class="organization-credential-data__text" v-show="form.email">
        Вам прийде лист із посиланням для підтвердження на адресу {{ form.email }} для завершення реєстрації
      </p>
      <div class="organization-credential-data__next-step">
        <TurnstileWidget
          ref="turnstile"
          @token-received="handleTurnstileSuccess"
          @token-expired="handleTurnstileExpired"
        />
        <OrangeFancyButton
          class="organization-credential-data__next-step--button"
          type="submit"
          :disabled="!canSwitchToNextStep || isRegisterLoading"
        >
          <SpinnerLoader
            v-show="isRegisterLoading"
            class="organization-credential-data__controls--loader"
            color="#FFFFFF"
          />
          Далі
        </OrangeFancyButton>
        <p class="organization-credential-data__error-message" v-if="getRegisterError">{{ getRegisterError }}</p>
        <a
          class="organization-credential-data__prev-step--button"
          @click="switchPreviousStep"
        >
          Назад
        </a>
      </div>
      <div class="organization-credential-data__login">
        <p class="organization-credential-data__login-text">
          У вас вже є аккаунт?
          <SmartLink
            class="organization-credential-data__link--login"
            :route="{ name: 'login' }"
          >
            Увійти
          </SmartLink>
        </p>
      </div>
    </form>
  </FormContainer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import StatusProgress from '../status-progress';
import { FormContainer } from '@/shared/ui/account';
import { LabeledTextInput, OrangeFancyButton } from '@/shared/ui/form';
import { SmartLink } from '@/shared/ui/link';
import { SpinnerLoader } from '@/shared/ui/loader';
import TurnstileWidget from '@/shared/ui/turnstile/TurnstileWidget';

export default {
  data() {
    return {
      form: {
        email: '',
      },
      errors: {
        email: '',
      },
      cfToken: '',
    };
  },
  created() {
    this.form.email = this.getAccountEmail;
  },
  components: { SpinnerLoader, FormContainer, StatusProgress, LabeledTextInput, OrangeFancyButton, SmartLink, TurnstileWidget },
  computed: {
    ...mapGetters('registerModule', [
      'getCurrentStep', 'getAccountEmail', 'isRegisterLoading', 'getRegisterError'
    ]),
    canSwitchToNextStep() {
      return !!this.form.email && !!this.cfToken;
    },
  },
  methods: {
    ...mapActions('registerModule', [
      'switchCurrentStep', 'switchPreviousStep', 'setAccountOrganizationCredentials', 'register',
    ]),
    clearError(field) {
      this.errors[field] = '';
    },
    validateEmail(email) {
      if (!email || email.trim().length === 0) {
        return 'Email є обов\'язковим';
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return 'Введіть коректну email адресу';
      }
      return '';
    },
    validateForm() {
      this.errors.email = this.validateEmail(this.form.email);

      return !this.errors.email;
    },
    handleTurnstileSuccess(token) {
      this.cfToken = token;
    },
    handleTurnstileExpired() {
      this.cfToken = '';
    },
    async handleSubmit() {
      if (this.validateForm()) {
        this.setAccountOrganizationCredentials({
          email: this.form.email,
          cfToken: this.cfToken,
        });

        try {
          await this.register();
        } catch (_) {
          if (this.$refs.turnstile) {
            this.$refs.turnstile.reset();
            this.cfToken = '';
          }
          if (this.getRegisterError) {
            return;
          }
        }

        this.switchCurrentStep();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.register__organization-credential-data-form {
  &__form-container {
    margin: 170px auto 0;
    width: 600px;

    @media (max-width: 630px) {
      margin: 50px 16px 0;
      width: auto;
    }
  }
}

.organization-credential-data-form {
  &__title {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 36px;
    font-weight: 400;
    color: #0C1421;
    text-align: center;
    margin-bottom: 16px;

    @media (max-width: 630px) {
      font-size: 24px;
    }
  }

  &__description {
    font-size: 18px;
    font-weight: 400;
    color: #313957;
    text-align: center;

    @media (max-width: 630px) {
      font-size: 14px;
    }
  }

  &__progress {
    margin-bottom: 16px;
  }
}

.organization-credential-data {
  &__input {
    &--telephone, &--email {
      margin-bottom: 16px;
    }
  }

  &__text {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #313957;
    margin-bottom: 32px;
  }

  &__next-step {
    margin-bottom: 32px;
  }

  &__prev-step {
    &--button {
      display: inline-block;
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      font-weight: 400;
      color: #313957;
      padding: 16px 24px;
      width: 100%;
      text-align: center;
      cursor: pointer;
    }
  }

  &__login {
    text-align: center;
  }

  &__link {
    &--login {
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      font-weight: 400;
      color: #6171E4;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__controls {
    &--loader {
      margin-right: 12px;
    }
  }

  &__error-message {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #FF3B30;
    margin-top: 16px;
  }
}

::v-deep .input-label,
.input-label {
  position: relative;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: #494E53;
  margin-bottom: 8px;
}

::v-deep .input-text, .input-text {
  position: relative !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 22px !important;
  font-weight: 400 !important;
  background-color: #EBF2FF !important;
  width: 515px !important;
  border-radius: 16px !important;
  border: none !important;
  color: #494E53 !important;
  padding: 16px 24px !important;

  @media (max-width: 1300px) {
    width: 100% !important;
  }

  &::placeholder {
    color: #8E9DB7 !important;
  }

  &--has-error {
    border: 2px solid #FF3B30 !important;
  }
}

::v-deep .orange-fancy-button__content {
  display: flex;
  justify-content: center;
}
</style>
